import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { confirmAlert } from 'react-confirm-alert';
import ConfirmPrompt from '../General/ConfirmPrompt';
import cn from 'classnames';
import './ProductAllLinksModal.scss';

import { isLoggedIn } from '../../Helpers/user_helpers';
import { formatAmountInCurrency, getPrettyTimeAgoFromNow } from '../../Helpers/formatting';
import { addVariablesToShopAffiliateLink } from '../../Helpers/attribution_helpers';
import {
  isLinkInStock,
  isLinkOutOfStock,
  isLinkStockUnknown,
  getLinkPriceDisplay,
  getLinkStandardPriceDisplay,
  isNonProductPageLink,
  isLinkDiscounted
} from '../../Helpers/shop_helpers';
import { postCatalogIssue } from '../../APIClient/shop';

import Modal from '../General/Modal';
import SmartImage from '../General/SmartImage';
import Tooltip from '../General/Tooltip';

const ProductAllLinksModal = props => {
  const { product, user, shop, close } = props;
  const { links } = product;

  // Featured Links
  const defaultLink = links[0];
  const [selectedLink, setPrimaryLink] = React.useState(defaultLink);
  const [isPrimaryLinkLocked, setIsPrimaryLinkLocked] = React.useState(false);
  const { merchant, url_data } = selectedLink;
  const { primaryImageUrl, productPrice, productCurrency, productStandardPrice, productDescription } = url_data;
  const formattedProductPrice =
    (productPrice && formatAmountInCurrency(productPrice, productCurrency)) ||
    (productStandardPrice && formatAmountInCurrency(productStandardPrice, productCurrency));
  const resetToDefaultLink = () => !isPrimaryLinkLocked && setPrimaryLink(defaultLink);

  // Check if the primary link is the default link
  const isShowingDefaultLink = selectedLink.link === defaultLink.link;
  const productImage = product?.images?.find(image => image.isCover)?.image || product?.images?.[0]?.image;
  const featuredImage = isShowingDefaultLink && !isPrimaryLinkLocked ? productImage : primaryImageUrl || productImage;

  // Columns
  const columns = [
    {
      display: 'Image',
      getSrc: link => link.url_data?.primaryImageUrl,
      className: 'image hide-on-mobile'
    },
    {
      display: 'Retailer',
      onClick: link => console.info(link),
      getValue: link => (link.merchant ? link.merchant.name : link.domain),
      className: 'retailer'
    },
    {
      display: 'Item',
      getValue: link => link.url_data?.variantName || '-',
      className: 'variant-name hide-on-mobile'
    },
    {
      display: 'Price',
      getValue: link =>
        (isLinkDiscounted(link) ? (
          <>
            <span className='standard-price'>{getLinkStandardPriceDisplay(link)}</span> <span className='price'>{getLinkPriceDisplay(link)}</span>
          </>
        ) : (
          getLinkPriceDisplay(link)
        )) || '-',
      getSortValue: link => link.url_data?.productPrice || null,
      sortDirections: ['asc', 'desc'],
      className: 'price'
    },
    {
      display: 'Stock',
      getTooltip: link =>
        link.url_data?.stockUpdatedAt ? `Last updated ${getPrettyTimeAgoFromNow(link.url_data?.stockUpdatedAt)}` : 'No Update Date',
      getValue: link =>
        isNonProductPageLink(link) ? 'Non-Product' : isLinkInStock(link) ? 'In Stock' : isLinkStockUnknown(link) ? 'Unknown' : 'Out of Stock',
      getSortValue: link => (isLinkOutOfStock(link) ? 0 : isLinkInStock(link) ? 2 : 1),
      sortDirections: ['desc'],
      className: 'stock'
    },
    {
      display: 'Link',
      getValue: link => (isLinkOutOfStock(link) ? 'Out of Stock' : 'Shop Now'),
      getHref: link => addVariablesToShopAffiliateLink(link.affiliate_link, { product, shop, user }),
      className: 'link'
    }
  ];

  // Sorting
  const [sortColDisplay, setSortColDisplay] = React.useState(null);
  const [sortDirection, setSortDirection] = React.useState('asc');
  const sortFn = sortColDisplay ? columns.find(column => column.display === sortColDisplay).getSortValue : null;
  const sortedLinks = sortFn ? _.orderBy(links, [l => _.isNil(sortFn(l)), sortFn], ['asc', sortDirection]) : links;

  React.useEffect(() => {
    window.__ADD_EVENT__('Shop - Product All Links Modal Opened', {
      product_id: product.id,
      'Product Title': product.title,
      'Product Brand': product.brand?.name,
      merchant_id: merchant?.id,
      merchant_name: merchant?.name
    });
  }, []);

  return (
    <Modal
      close={close}
      showClose
      noPadding
      visible
      className='product-all-links-modal-outer-container'
      innerClassName='product-all-links-modal-inner-container'
      contentClassName='product-all-links-modal-content'
    >
      <div className='product-preview'>
        <div className={cn('product-preview-image', { cover: isShowingDefaultLink })}>
          {featuredImage && <SmartImage src={featuredImage} alt={product.title} noHoverScale fallbackImage={productImage} />}
        </div>
        <div className='product-preview-data'>
          <div className='price'>{formattedProductPrice || '-'}</div>
          <div className='brand'>{product.brand?.name || 'Unknown Brand'}</div>
          <div className='title'>{product.title}</div>
          <div className='description'>{productDescription}</div>
          <a
            href={selectedLink && addVariablesToShopAffiliateLink(selectedLink.affiliate_link, { product, shop, user })}
            target='_blank'
            rel='noopener noreferrer'
            className='buy-btn'
          >
            {merchant ? `Shop now at ${merchant.name}` : 'Shop now'}
          </a>
        </div>
      </div>
      <div className='links-preview' onMouseLeave={resetToDefaultLink}>
        <div className='links-preview-header'>
          <div className='title'>Where else to Buy</div>
          <div className='description'>Discover the best deals and where to buy your favorite items.</div>
        </div>
        <div className='links-table'>
          <div className='row header'>
            {columns.map(column => {
              const { display, getSortValue, sortDirections } = column;
              const isSorted = sortColDisplay === display;
              const isSortable = !!getSortValue;
              const onClick = () => {
                if (!isSortable) return;
                const shouldResetSort = isSorted && sortDirection === _.last(sortDirections);

                setSortColDisplay(shouldResetSort ? null : display);
                setSortDirection(shouldResetSort ? _.first(sortDirections) : isSorted ? _.last(sortDirections) : _.first(sortDirections));
              };
              return (
                <div onClick={onClick} className={cn('cell header', column.className, { sortable: isSortable })} key={column.display}>
                  {column.display}
                  {isSorted && <FontAwesomeIcon icon={sortDirection === 'asc' ? faArrowUp : faArrowDown} />}
                </div>
              );
            })}
          </div>
          {sortedLinks?.map((link, index) => {
            const isLockedToLink = link.link === selectedLink.link;
            const onMouseIn = () => !isPrimaryLinkLocked && setPrimaryLink(link);
            const onClick = () => {
              if (isPrimaryLinkLocked) {
                setPrimaryLink(link);
                setIsPrimaryLinkLocked(false);
              } else {
                setPrimaryLink(link);
                setIsPrimaryLinkLocked(true);
              }
            };

            const additionalClasses = {
              locked: isLockedToLink,
              'out-of-stock': isLinkOutOfStock(link),
              'non-product-page': isNonProductPageLink(link)
            };

            const reportIssue = () => {
              confirmAlert({
                customUI: ({ onClose }) => (
                  <ConfirmPrompt
                    header='Report an Issue.'
                    subheader='Please select the issue you are reporting.'
                    onCancel={onClose}
                    customInputFields={[
                      { label: 'Incorrect Price', isBoolean: true, value: 'incorrect_price' },
                      { label: 'Incorrect Stock', isBoolean: true, value: 'incorrect_stock' },
                      { label: 'Broken or Incorrect Link', isBoolean: true, value: 'broken_link' },
                      { label: 'Wish this was Primary Link', isBoolean: true, value: 'wrong_choice_of_primary_link' },
                      { placeholder: 'Anything else you would like to explain?', value: 'description' }
                    ]}
                    onSubmitAwait={async responseValues => {
                      const resp = await postCatalogIssue({
                        Product_id: product.id,
                        urlStem: link.link,
                        affiliate_link: addVariablesToShopAffiliateLink(link.affiliate_link, { product, shop, user }),
                        ...responseValues
                      });
                      resp.success
                        ? window.ALERT.success('Issue reported successfully, thank you!')
                        : window.ALERT.error('Error reporting issue, please try again.');
                      return true;
                    }}
                  />
                )
              });
            };

            return (
              <div className={cn('link row', additionalClasses)} key={link.link + index} onClick={onClick} onMouseEnter={onMouseIn}>
                {isLoggedIn(user) && (
                  <div className='report-issue' onClick={reportIssue}>
                    Issue?
                  </div>
                )}
                {columns.map(column => {
                  const value = column.getValue && column.getValue(link);
                  const href = column.getHref && column.getHref(link);
                  const src = column.getSrc && column.getSrc(link);
                  const tooltip = column.getTooltip && column.getTooltip(link);
                  return src ? (
                    <div className={cn('cell', column.className, additionalClasses)}>
                      <SmartImage src={src} alt={value} noHoverScale />
                    </div>
                  ) : href ? (
                    <a href={href} rel='noopener noreferrer' target='_blank' className={cn('cell', column.className, additionalClasses)}>
                      {value}
                    </a>
                  ) : (
                    <Tooltip message={tooltip}>
                      <div
                        onClick={() => column.onClick && column.onClick(link)}
                        className={cn('cell', column.className, additionalClasses)}
                        key={column.display}
                      >
                        {value}
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

ProductAllLinksModal.propTypes = {
  user: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,

  // From Outside
  product: PropTypes.object.isRequired,
  close: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { user, shop } = state;
  return { user, shop };
};

export default connect(mapStateToProps, {
  // functions go here
})(ProductAllLinksModal);
